/**
 * CampaignsSection — все кампании кабинета, разложенные по одному
 * признаку: что с ними делать.
 *
 * Ранняя версия обвешивала каждую кампанию спарклайном, списком сенсоров
 * и парой бейджей — и утонула в графике. Читателю тут нужен не разбор
 * кампании, а ответ на три вопроса: где теряются деньги, что держать под
 * контролем и что можно масштабировать. Поэтому у группы есть сумма и
 * фраза обычными словами, а у кампании — имя, расход и одна причина.
 *
 * Кампании приходят уже отсортированными (красные, жёлтые, зелёные,
 * серые, внутри цвета по убыванию расхода) — своей сортировки не
 * добавляем. Серые (без расхода за период) в светофоре не показываются:
 * колонка «ничего не тратили» не отвечает ни на один из трёх вопросов.
 *
 * @format
 */

import {useLayoutEffect, useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {CAMPAIGN_COLUMNS, paletteTone, type CampaignColumn, type PaletteTone} from '../content';
import {useReportFormat} from '../format';
import {CAMPAIGNS, MEASURE, TABULAR, pluralize} from '../helpers';
import type {Campaign} from '../types';
import {ReportCard} from './ReportCard';
import {StatusGlyph} from './StatusGlyph';

export interface CampaignsSectionProps {
  /** Плоский список: по колонкам их раскладывает сама секция. */
  campaigns: Campaign[];
}

const GroupTitle = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

const Amount = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone}>(({theme, $tone}) => ({
  ...text('heading.l'),
  color: theme.palette[$tone].main,
}));

/** Фраза, ради которой блок существует: что это значит и что делать. */
const Verdict = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

/** Значок держится строки заголовка, а не центра блока. */
const GlyphCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 'none',
  height: 28,
});

const Line = styled(Row)({
  paddingTop: spacingPx('s'),
  paddingBottom: spacingPx('s'),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${surfaceVar('surface2')}`,
  },
});

const Name = styled(Box)({
  /*
   * `body.l`, а не `label.l`: тот же кегль, но обычное начертание. Имя
   * кампании — это адрес, а не заголовок; полужирным в строке должна
   * набираться сумма, ради которой строка и читается. Роль меняется
   * целиком, а не переопределяется вес поверх неё.
   */
  ...text('body.l'),
  color: surfaceVar('onSurface'),
  flex: '1 1 auto',
  minWidth: 0,
  /*
   * Настоящее имя кампании — сорок с лишним знаков без единого пробела:
   * `МК_КАЗС_бизнес_автотаргетинг_25+_ПК_СПб_ПН-СР`. Рвать такую строку
   * браузеру негде, и она молча уезжала за край карточки — обрезалось до
   * 134 px, то есть последняя треть имени, где как раз стоит регион и
   * расписание.
   *
   * `anywhere`, а не `break-word`: только оно влияет на min-content и
   * позволяет флекс-элементу ужаться. С `break-word` строка перестала бы
   * обрезаться, но карточка всё равно распирала бы колонку.
   */
  overflowWrap: 'anywhere',
});

/**
 * Свёрнутая мелочь. Приглушена намеренно: это итог по нескольким
 * кампаниям, а не ещё одна кампания, и путать их не нужно.
 */
const TinyName = styled(Name)({
  color: surfaceVar('onSurfaceMuted'),
});

/** Деньги везде окрашены по своей группе — колонка читается цветом. */
const Spend = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone}>(({theme, $tone}) => ({
  ...text('label.l'),
  ...TABULAR,
  color: theme.palette[$tone].main,
  flex: 'none',
  whiteSpace: 'nowrap',
}));

/**
 * Что группа значит для денег — одной фразой, без терминов. Формулировка
 * зависит от того, теряем мы на этих кампаниях или недобираем.
 */
function verdictFor(column: CampaignColumn, spend: string): string {
  if (column.color === 'RED') {
    return `На этих кампаниях уходит ${spend}, и эти деньги работают не так, как задумано. Это первое, что стоит исправить.`;
  }
  if (column.color === 'YELLOW') {
    return `Кампании работают, но обращение обходится дороже плана. ${spend} расхода приносят меньше клиентов, чем могли бы.`;
  }
  return `Здесь всё в порядке: обращение дешевле плана, ошибок нет. Именно эти ${spend} имеет смысл увеличивать.`;
}

/** Внутренний отступ карточки — от него считается вылет кнопки. */
const CARD_PADDING = spacingPx('l');

/**
 * Кнопка «Ещё N» завершает карточку собой.
 *
 * Гасит отступы карточки слева, справа и снизу: свёрнутый список должен
 * упираться в её край, а не висеть плашкой внутри. Скругление снизу даёт
 * сама карточка, обрезая углы, — поэтому у кнопки его нет.
 */
const MoreSlot = styled(Box)({
  marginLeft: -CARD_PADDING,
  marginRight: -CARD_PADDING,
  marginBottom: -CARD_PADDING,
  marginTop: spacingPx('m'),
});

/**
 * Карточка группы.
 *
 * По высоте не растягивается: колонка из двух кампаний не обязана быть
 * ростом с колонку из сорока. Растянутая, она догоняла соседа пустотой, и
 * кнопка «Ещё» повисала в её середине.
 *
 * А вот по ширине растянуться обязана — но только когда ряд схлопнулся в
 * столбец. `Row` в этом режиме ставит `align-items: flex-start`, и карточки
 * ужимаются до самого длинного имени кампании, давая столбец рваной ширины.
 * Отсюда медиазапрос: он повторяет порог `reflowAt='lg'` у ряда — своего
 * значения `Row` наружу не отдаёт.
 */
const REFLOW_LG = 1024;

const GroupCard = styled(ReportCard)({
  // Равная доля ряда вместо ширины по содержимому: база `0` не зависит
  // от того, какие имена сейчас отрисованы, поэтому раскрытие списка
  // больше не двигает соседние колонки.
  flex: '1 1 0',
  minWidth: 0,
  alignSelf: 'flex-start',
  [`@media (max-width: ${REFLOW_LG}px)`]: {
    // В столбце главная ось вертикальная: там доля ряда смысла не имеет,
    // высота должна остаться по содержимому.
    flex: '0 0 auto',
    alignSelf: 'stretch',
  },
});

/**
 * Сколько кампаний показывать сразу и с какой длины список сворачивать.
 *
 * На боевом отчёте в красной колонке оказалось 173 кампании — карточка
 * выросла до десяти тысяч пикселей, и этаж «светофор» перестал быть
 * светофором: до жёлтой и зелёной колонок читатель просто не доезжал.
 *
 * Порог выше показываемого числа намеренно: сворачивать список из пяти
 * ради одной спрятанной строки — обмен видимости на клик без выигрыша.
 */
const VISIBLE = 4;
const COLLAPSE_FROM = 6;

/**
 * Ниже этого расхода кампания не заслуживает собственной строки.
 *
 * Тысяча рублей за период — это не решение, а шум: поимённо такую кампанию
 * не открывают и бюджет по ней не двигают. Но и выбросить её нельзя, иначе
 * сумма строк перестанет сходиться с итогом колонки.
 */
const TINY_SPEND = 1_000;

/** Одну мелкую кампанию сворачивать не в чем — строка та же, смысла меньше. */
const FOLD_FROM = 2;

/** Строка списка: либо кампания, либо свёрнутая мелочь. */
type ListRow =
  | {kind: 'campaign'; campaign: Campaign}
  | {kind: 'tiny'; count: number; total: number};

/**
 * Складывает список колонки: крупные кампании по одной, мелочь — последней
 * строкой. Сумма мелочи считается по всем свёрнутым, а не по видимым:
 * строка обязана объяснять, куда делись остальные деньги колонки.
 */
function buildRows(campaigns: Campaign[]): ListRow[] {
  const tiny = campaigns.filter(campaign => (campaign.spend ?? 0) < TINY_SPEND);
  if (tiny.length < FOLD_FROM) {
    return campaigns.map(campaign => ({kind: 'campaign', campaign}));
  }
  const big = campaigns.filter(campaign => (campaign.spend ?? 0) >= TINY_SPEND);
  const total = tiny.reduce((sum, campaign) => sum + (campaign.spend ?? 0), 0);
  return [
    ...big.map(campaign => ({kind: 'campaign' as const, campaign})),
    {kind: 'tiny' as const, count: tiny.length, total},
  ];
}

/**
 * Строка списка кампаний. Свёрнутая мелочь набрана приглушённо: это не
 * кампания, а итог по нескольким, и путать их не нужно.
 */
/** Ключ строки: у кампании свой id, свёрнутая мелочь в списке одна. */
function rowKey(row: ListRow, index: number): string {
  return row.kind === 'campaign' ? row.campaign.campaignId : `tiny-${index}`;
}

function CampaignRow({row, tone}: {row: ListRow; tone: PaletteTone}) {
  const fmt = useReportFormat();
  return (
    <Line gap='l' alignY='baseline' reflowAt='sm'>
      {row.kind === 'campaign' ? (
        <Name>{row.campaign.name ?? row.campaign.campaignId}</Name>
      ) : (
        <TinyName>Ещё {pluralize(row.count, CAMPAIGNS)} меньше {fmt.money(TINY_SPEND)}</TinyName>
      )}
      <Spend $tone={tone}>
        {fmt.money(row.kind === 'campaign' ? (row.campaign.spend ?? 0) : row.total)}
      </Spend>
    </Line>
  );
}

function Group({column, campaigns}: {column: CampaignColumn; campaigns: Campaign[]}) {
  const fmt = useReportFormat();
  const tone = paletteTone(column.tone);
  const spend = fmt.money(campaigns.reduce((sum, campaign) => sum + (campaign.spend ?? 0), 0));
  const rows = buildRows(campaigns);
  const collapse = rows.length >= COLLAPSE_FROM;
  const shown = collapse ? rows.slice(0, VISIBLE) : rows;
  const rest = collapse ? rows.slice(VISIBLE) : [];
  // Строка свёрнутой мелочи весит столько кампаний, сколько в неё сложено.
  const hidden = rest.reduce(
    (sum, row) => sum + (row.kind === 'campaign' ? 1 : row.count),
    0,
  );
  const [open, setOpen] = useState(false);
  const slotRef = useRef<HTMLDivElement | null>(null);
  /** Где стоял низ слота в момент клика; null — когда удерживать нечего. */
  const anchorBottom = useRef<number | null>(null);

  /**
   * Возвращает прокрутку после сворачивания снизу.
   *
   * Верхней кнопке это не нужно: она и так стоит там, где стояла «Ещё»,
   * и содержимое исчезает ПОД ней. А нижняя убирает пиксели над собой —
   * и без поправки прыжок равен всей высоте списка.
   *
   * Держим низ слота, а не верх карточки: под курсором в этот момент
   * именно кнопка, и вернуть на место надо её.
   */
  const collapseFromBottom = () => {
    anchorBottom.current = slotRef.current?.getBoundingClientRect().bottom ?? null;
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (anchorBottom.current === null) return;
    const now = slotRef.current?.getBoundingClientRect().bottom;
    // Правку делаем до отрисовки кадра, поэтому смещения не видно.
    if (now !== undefined) window.scrollBy(0, now - anchorBottom.current);
    anchorBottom.current = null;
  }, [open]);

  return (
    <GroupCard padding='l' accent={tone} clip>
      <Stack gap='l'>
        {/* Заголовок, сумма и вывод — три ступени одной мысли, но каждая
            должна успеть прочитаться отдельно. */}
        <Stack gap='m'>
          {/* Выравнивание по верху: если заголовок переносится на две
              строки, значок обязан остаться у первой, а не съехать к центру. */}
          <Row gap='s' alignY='start'>
            <GlyphCell>
              <StatusGlyph tone={tone} />
            </GlyphCell>
            <GroupTitle>{column.title}</GroupTitle>
          </Row>
          <Amount $tone={tone}>{spend}</Amount>
          <Verdict>{verdictFor(column, spend)}</Verdict>
        </Stack>

        <Stack gap='none'>
          {/* Зазор между именем и суммой крупнее строчного: имя переносится
              на две строки, сумма не переносится вовсе, и на восьми пикселях
              они читались одним куском. */}
          {shown.map((row, index) => (
            <CampaignRow key={rowKey(row, index)} row={row} tone={tone} />
          ))}
        </Stack>

        {rest.length > 0 && (
          <MoreSlot ref={slotRef}>
            {/* Верхняя кнопка появляется ровно там, где стояла «Ещё»:
                слот у обеих один, и она — его первый ребёнок. Курсор
                после раскрытия остаётся над кнопкой, и второй клик
                закрывает список, не заставляя листать сорок строк. */}
            {open && (
              <Button
                size='m'
                variant='ghost'
                color='neutral'
                fullWidth
                rounded={false}
                rightIcon='keyboard_arrow_up'
                onClick={() => setOpen(false)}
              >
                Свернуть
              </Button>
            )}
            {/* Хвост списка — между двумя кнопками. */}
            {open && (
              <Box style={{paddingLeft: CARD_PADDING, paddingRight: CARD_PADDING}}>
                <Stack gap='none'>
                  {rest.map((row, index) => (
                    <CampaignRow key={rowKey(row, index)} row={row} tone={tone} />
                  ))}
                </Stack>
              </Box>
            )}
            {/* Нижняя завершает карточку — она же «Ещё» в свёрнутом виде. */}
            <Button
              size='m'
              variant='ghost'
              color='neutral'
              fullWidth
              rounded={false}
              rightIcon={open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              onClick={() => (open ? collapseFromBottom() : setOpen(true))}
            >
              {open ? 'Свернуть' : `Ещё ${hidden}`}
            </Button>
          </MoreSlot>
        )}
      </Stack>
    </GroupCard>
  );
}

export function CampaignsSection({campaigns}: CampaignsSectionProps) {
  return (
    <Row gap='m' alignY='start' reflowAt='lg'>
      {CAMPAIGN_COLUMNS.map(column => {
        const inColumn = campaigns.filter(campaign => campaign.color === column.color);
        // Пустая колонка не рисуется: «Именно эти 0 ₽ имеет смысл
        // увеличивать» — не вывод, а следствие пустого множества.
        if (inColumn.length === 0) return null;
        return <Group key={column.color} column={column} campaigns={inColumn} />;
      })}
    </Row>
  );
}
